/**
 * The single timestamp-formatting utility for the SPA (spec 08 §9, decision
 * 2026-08-21, item 1.11).
 *
 * Storage and the API are UTC everywhere (spec 01 §6); presentation is
 * per-user. Every timestamp the UI shows must therefore be rendered in the
 * signed-in user's timezone *with the zone named* (e.g. "24 Aug 2026, 17:04
 * SAST") so an operator never mistakes a UTC time for a local one when judging a
 * deployment window. These functions are pure (the zone is passed in) so they
 * are trivially testable; the React-facing wrappers are `useDisplayTimeZone` /
 * `useTimestampFormatter` and the `<Timestamp>` component.
 */

/**
 * The zone used when neither the user's stored preference nor the browser can
 * supply a valid one. UTH operates in SAST (spec 01 §6), so that is the
 * least-surprising default for its operators.
 */
export const FALLBACK_TIMEZONE = 'Africa/Johannesburg';

/**
 * Display shapes used across the app. Keeping the set small and named (rather
 * than letting each screen pass raw `Intl` options) is what keeps timestamps
 * consistent from page to page.
 *
 * - `datetime` — full date + time, e.g. "24 Aug 2026, 17:04 SAST" (the default).
 * - `datetime-short` — no year, for dense tables, e.g. "24 Aug, 17:04 SAST".
 * - `date` — date only, e.g. "24 Aug 2026" (still resolved in the user's zone,
 *   because a UTC instant near midnight can fall on a different local day).
 * - `time` — time with seconds, for step timelines and logs.
 * - `clock` — hours and minutes only, for calendar chips.
 */
export type TimestampFormat = 'datetime' | 'datetime-short' | 'date' | 'time' | 'clock';

/** `Intl` options for each {@link TimestampFormat}. */
const FORMAT_OPTIONS: Record<TimestampFormat, Intl.DateTimeFormatOptions> = {
  datetime: {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  },
  'datetime-short': {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  },
  date: { year: 'numeric', month: 'short', day: 'numeric' },
  time: { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZoneName: 'short' },
  clock: { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' },
};

/**
 * Whether the runtime recognises `zone` as an IANA timezone. `Intl` throws a
 * `RangeError` for unknown zones, which is the most reliable check available
 * without shipping a zone database.
 */
export function isValidTimeZone(zone: string): boolean {
  if (zone === '') {
    return false;
  }
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The browser's own timezone, or null if the runtime cannot report a valid one
 * (some locked-down or very old environments return `undefined`).
 */
export function browserTimeZone(): string | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && isValidTimeZone(zone) ? zone : null;
  } catch {
    return null;
  }
}

/**
 * Pick the zone to display timestamps in: the user's stored preference when it
 * is valid, otherwise the browser's zone, otherwise {@link FALLBACK_TIMEZONE}.
 * A stale or corrupted preference must degrade to something sensible rather
 * than crash every table on the page with a `RangeError`.
 */
export function resolveTimeZone(preferred: string | null | undefined): string {
  if (typeof preferred === 'string' && isValidTimeZone(preferred)) {
    return preferred;
  }
  return browserTimeZone() ?? FALLBACK_TIMEZONE;
}

/** Parse an ISO-8601 string, returning null when it is not a real instant. */
function parseInstant(iso: string): Date | null {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Format an ISO-8601 instant in `timeZone` using a named {@link TimestampFormat}.
 *
 * Unparseable input is returned verbatim: showing the raw API value is more
 * useful to someone debugging than "Invalid Date", and it never throws.
 */
export function formatTimestamp(
  iso: string,
  timeZone: string,
  format: TimestampFormat = 'datetime',
): string {
  const date = parseInstant(iso);
  if (date === null) {
    return iso;
  }
  return date.toLocaleString(undefined, { ...FORMAT_OPTIONS[format], timeZone });
}

/**
 * The same instant as an unambiguous UTC string ("2026-08-24 15:04:05 UTC"),
 * used as the hover tooltip so any displayed time can be cross-checked against
 * server logs and audit records, which are all UTC. Returns null for input that
 * is not a real instant (there is nothing trustworthy to show).
 */
export function formatUtc(iso: string): string | null {
  const date = parseInstant(iso);
  if (date === null) {
    return null;
  }
  return `${date.toISOString().slice(0, 19).replace('T', ' ')} UTC`;
}
